<template>
  <div class="max-w-6xl mx-auto p-6 space-y-8">

    <!-- Breadcrumb -->
    <nav class="text-sm text-gray-600 flex items-center gap-2">
      <span>{{ moduleTitle }}</span>
      <span>›</span>
      <span>{{ unitTitle }}</span>
      <span>›</span>
      <span>{{ lessonTitle }}</span>
      <span class="text-gray-400">›</span>
      <span class="font-semibold text-gray-900">{{ currentSectionTitle }}</span>
    </nav>

    <!-- Page Header -->
    <header class="flex items-center justify-between">
      <h1 class="text-2xl font-bold text-gray-900">
        {{ currentSectionTitle }}
      </h1>

      <button
        class="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
        @click="goBackToCurriculum"
      >
        Back to Curriculum
      </button>
    </header>

    <div class="grid grid-cols-12 gap-6">

      <!-- LEFT PANEL: Lesson Outline -->
      <aside class="col-span-12 md:col-span-3 space-y-4">

        <Card class="p-4">
          <h2 class="text-lg font-semibold mb-3">{{ lessonTitle }}</h2>

          <div class="space-y-2">
            <div
              v-for="sub in lesson.subsections"
              :key="sub.content"
              class="p-2 rounded cursor-pointer flex items-center justify-between"
              :class="{
                'bg-brand-50 text-brand-700 border border-brand-200':
                  sub.content === sectionGroupId,
                'hover:bg-gray-100': sub.content !== sectionGroupId
              }"
              @click="goToSection(sub.content)"
            >
              <span>{{ sub.title }}</span>
              <span class="text-xs text-gray-500"
                >{{ sectionProgress(sub.content) }}%</span
              >
            </div>
          </div>
        </Card>

      </aside>

      <!-- CENTER PANEL: Question / Practice Session -->
      <main class="col-span-12 md:col-span-6 space-y-6">

        <Card class="p-4 min-h-[300px] flex flex-col gap-4">

          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">Practice Questions</h2>
            <span class="text-gray-500 text-sm"
              >Accuracy: {{ sectionProgress(sectionGroupId) }}%</span
            >
          </div>

          <!-- Question content injected here -->
          <div v-if="currentQuestion" class="space-y-4">
            <div
              class="text-lg leading-relaxed"
              v-html="currentQuestion.text"
            ></div>

            <div class="space-y-2">
              <button
                v-for="choice in currentQuestion.choices"
                :key="choice.id"
                class="w-full p-3 border rounded-lg text-left hover:bg-gray-50"
                @click="submitAnswer(choice)"
              >
                {{ choice.text }}
              </button>
            </div>
          </div>

          <div v-else class="text-gray-500 text-center py-20">
            Loading questions...
          </div>
        </Card>

      </main>

      <!-- RIGHT PANEL: Navigation -->
      <aside class="col-span-12 md:col-span-3">

        <Card class="p-4 space-y-4">
          <h2 class="text-lg font-semibold">Navigation</h2>

          <button
            v-if="prevSectionGroup"
            class="w-full bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg"
            @click="goToSection(prevSectionGroup)"
          >
            ‹ Previous
          </button>

          <button
            v-if="nextSectionGroup"
            class="w-full bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg"
            @click="goToSection(nextSectionGroup)"
          >
            Next ›
          </button>

          <button
            v-if="!nextSectionGroup"
            class="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
            @click="finishLesson"
          >
            Finish Lesson
          </button>
        </Card>

      </aside>

    </div>
  </div>
</template>

<script setup>
  // TODO: DELETE?
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Card from '@/components/common/Card.vue';

import { useCurriculumStore } from '@/stores/curriculumStore';
import { useProgressStore } from '@/stores/progressStore';
import { usePracticeSession } from '@/composables/usePracticeSession';
import { useStudentStore } from '@/stores/studentStore';

const router = useRouter();
const route = useRoute();

const studentStore = useStudentStore();
const curriculumStore = useCurriculumStore();
const progressStore = useProgressStore();

const sectionGroupId = route.params.sectionId;
const studentId = route.params.studentId;

// Load student & track
const student = computed(() =>
  studentStore.students.find(s => s.id === studentId)
);


onMounted(async () => {
  if (!curriculumStore.lessons.length) {
    await curriculumStore.loadTrack(student.value.track || 'core');
  }
});

// Flattened map of all section groups with metadata
const allSG = computed(() => curriculumStore.allSectionGroups);

// Session manager (you already have this)
const {
  currentQuestion,
  submitAnswer,
  loadSectionQuestions
} = usePracticeSession(sectionGroupId, studentId);

// Load questions
onMounted(() => {
  loadSectionQuestions(sectionGroupId);
});

// --- FIND MODULE / UNIT / LESSON CONTEXT ---
const lesson = computed(() => getLessonForSection(sectionGroupId));
const subsection = computed(() => {
  const l = lesson.value;
  if (!l || !Array.isArray(l.subsections)) return null;
  return l.subsections.find(sub => sub.content === sectionGroupId) || null;
});
const moduleTitle = computed(() => lesson.value?.moduleTitle || '');
const unitTitle = computed(() => lesson.value?.unitTitle || '');
const lessonTitle = computed(() => lesson.value?.title || '');
const currentSectionTitle = computed(() => subsection.value?.title || sectionGroupId);

// --- LOOKUP NAME FROM SECTION GROUP (your spreadsheet values) ---
function lookupSectionGroupName(sgId) {
  // Try to find the subsection title in the lesson
  const l = getLessonForSection(sgId);
  if (l && Array.isArray(l.subsections)) {
    const sub = l.subsections.find(sub => sub.content === sgId);
    if (sub && sub.title) return sub.title;
  }
  // fallback
  const entry = allSG.value.find(e => e.sectionGroupId === sgId);
  return entry?.lessonTitle || sgId;
}

// --- PROGRESS ---
function sectionProgress(sgId) {
  const p = progressStore.progress[sgId];
  return p ? Math.round(p.accuracy * 100) : 0;
}

// --- NEXT/PREV SECTION LOGIC ---

const nextSectionGroup = computed(() => {
  const lesson = getLessonForSection(sectionGroupId);
  if (!lesson || !Array.isArray(lesson.subsections)) return null;
  const idx = lesson.subsections.findIndex(sub => sub.content === sectionGroupId);
  if (idx === -1) return null;
  return lesson.subsections[idx + 1]?.content || null;
});

const prevSectionGroup = computed(() => {
  const lesson = getLessonForSection(sectionGroupId);
  if (!lesson || !Array.isArray(lesson.subsections)) return null;
  const idx = lesson.subsections.findIndex(sub => sub.content === sectionGroupId);
  if (idx <= 0) return null;
  return lesson.subsections[idx - 1]?.content || null;
});

function getLessonForSection(sgId) {
  for (const mod of curriculumStore.modules) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        if (Array.isArray(lesson.subsections) && lesson.subsections.some(sub => sub.content === sgId)) {
          return lesson;
        }
      }
    }
  }
  return null;
}

// --- NAVIGATION ---

function goToSection(sgId) {
  router.push(`/student/learn/${sgId}`);
}

function goBackToCurriculum() {
  router.push(`/student/curriculum/${studentId}`);
}

function finishLesson() {
  router.push(`/student/curriculum/${studentId}`);
}
</script>
